import { useCallback, useEffect, useMemo, useRef } from "react";
import { connect, ConnectedProps } from "react-redux";
import moment from "moment";
import { AppDispatch, RootState } from "store";
import { General } from "./General";
import { actions, regionSelector } from "reducers/regions";
import { PolygonGeneralContainerProps, PolygonGeneralForm } from "./types";
import { validationSchema } from "./schemas";
import { getTimezoneOffset } from "utils/date";
import { actions as notificationActions } from "reducers/notification";
import { actions as sessionActions, sessionSelector } from "reducers/session";
import { NotificationStateType } from "reducers/types/notification";
import { FormApi } from "final-form";
import { DATE_FORMATS } from "utils/constants";
import { useTranslation } from "react-i18next";
import { dirtyAlert } from "utils/alerts";
import { FormRefType } from "utils/types";

const GeneralContainer = (
  props: PolygonGeneralContainerProps & ConnectorProps
): JSX.Element => {
  const { t } = useTranslation();
  const disableForm = useMemo(
    () => !props.region || props.filters?.updated,
    [props]
  );
  const formRef: FormRefType<PolygonGeneralForm> = useRef(null);

  useEffect(() => {
    if (props.filters?.region) {
      props.getRegionDetail(props.filters?.region?.id);
      props.getNextRain(props.filters?.region?.id);
    }
    return () => props.setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatHour = useCallback(
    (hour: string) => {
      const offset = getTimezoneOffset(Number(props.region?.config?.timeZone));
      return hour
        ? moment(hour, DATE_FORMATS.fullHour)
            .utcOffset(offset)
            .format(DATE_FORMATS.shortHour)
        : "";
    },
    [props.region?.config?.timeZone]
  );

  const initialValues = useMemo(() => {
    const config = props.region?.config;
    const { initial, end } = config?.deliveryOperationTime || {
      initial: "",
      end: "",
    };

    const values: PolygonGeneralForm = {
      id: props.region?.id,
      distance: {
        maxBetweenPoints: config?.distance?.maxBetweenPoints || "-",
        maxDistanceForBoundary: config?.distance?.maxDistanceForBoundary || "-",
      },
      deliveryOperationTime: {
        initial: formatHour(initial),
        end: formatHour(end),
      },
      trip: {
        ...config?.trip,
        maximumTravelDistance: config?.trip?.maximumTravelDistance || "-",
      },
      pickUpMaxCapacity: config?.pickUpMaxCapacity || "-",
    };
    return values;
  }, [formatHour, props.region?.config, props.region?.id]);

  const onSubmit = (values: PolygonGeneralForm) => {
    props.updateRegionDetail(values);
  };

  const editWorkzones = (dirty: boolean, formReset: () => void) => {
    const openModal = () => {
      props.toggleModalVisibility(true);
      formReset();
    };
    if (!dirty) openModal();
    else showRestoreNotification(openModal);
  };

  const restore = (isDirty: boolean, form: FormApi<object, object>) => {
    const restart = () => form.reset();
    if (isDirty) showRestoreNotification(restart);
    else restart();
  };

  useEffect(() => {
    if (formRef.current) formRef.current.restart();
  }, [disableForm]);

  const showRestoreNotification = (onClose: Function) => {
    return props.showNotification(
      dirtyAlert({
        onCancel: onClose,
        message: t("alerts:changesWillBeLostMessage"),
      })
    );
  };

  return (
    <General
      {...props}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      editWorkzones={editWorkzones}
      restore={restore}
      disableForm={disableForm}
      formRef={formRef}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  showModal: regionSelector(state).showModal,
  showRainModal: regionSelector(state).showRainModal,
  region: regionSelector(state).actualRegion,
  filters: regionSelector(state).filters,
  nextRain: regionSelector(state).nextRain,
  countryCode: sessionSelector(state).countryCode,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  getRegionDetail: (regionId: string) => {
    dispatch(actions.getRegionDetailRequest(regionId));
  },
  updateRegionDetail: (params: PolygonGeneralForm) => {
    dispatch(actions.updateRegionDetailRequest(params));
  },
  toggleModalVisibility: (show: boolean) => {
    dispatch(actions.toggleModalVisibility(show));
  },
  toggleRainModalVisibility: (show: boolean) => {
    dispatch(actions.toggleRainModalVisibility(show));
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  getNextRain: (regionId: string) => {
    dispatch(actions.getNextRainRequest(regionId));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(GeneralContainer);
